import React, { useEffect } from "react";
import { useUser, PricingTable } from "@clerk/clerk-react";
import { Upload, Mail, Cpu, Eye } from "lucide-react";
import useBillingStore from "../../store/useBillingStore";

const getProgressClasses = (pct) => {
  if (pct >= 90) return "from-red-500 to-rose-600";
  if (pct >= 60) return "from-amber-500 to-orange-600";
  return "from-emerald-500 to-green-600";
};

const UsageCard = ({ title, used = 0, limit = 0, Icon }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const gradient = getProgressClasses(pct);
  const exceeded = limit > 0 && used >= limit;
  const trackClasses = exceeded ? "bg-red-100 border-red-200" : "bg-gray-100 border-gray-200";
  const barClasses = exceeded ? "bg-red-500" : `bg-gradient-to-r ${gradient}`;
  return (
    <div className="relative p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow min-h-[140px] flex flex-col justify-between overflow-visible">
      {/* Exceeded badge */}
      {exceeded && (
        <span className="absolute -top-2 -right-2 px-2 py-1 text-[11px] font-semibold rounded-full bg-red-100 text-red-700 border border-red-200 shadow-sm">
          Exceeded
        </span>
      )}

      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
          {Icon && <Icon className="h-5 w-5 text-gray-700" />}
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 leading-5">{title}</h3>
          <div className="mt-1 text-xs text-gray-600">
            <span className="font-semibold text-gray-900">{used}</span>
            <span className="mx-1">/</span>
            <span>{limit}</span>
            {limit > 0 && (
              <span className="ml-2 text-gray-400">({pct}%)</span>
            )}
          </div>
        </div>
      </div>

      <div className={`mt-1 h-2 rounded-full ${trackClasses} overflow-hidden`}>
        <div className={`h-full ${barClasses}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function Billing() {
  const { user, isLoaded } = useUser();
  const { billingInfo, loading, fetchBillingInfo } = useBillingStore();

  useEffect(() => {
    if (isLoaded) {
      fetchBillingInfo();
    }
  }, [isLoaded, fetchBillingInfo]);

  if (!isLoaded || (loading && !billingInfo)) {
    return <div className="flex items-center justify-center h-screen text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage Your Subscription</h1>
      <p className="text-gray-500 mb-6">
        You are signed in as <span className="font-medium">{user.primaryEmailAddress?.emailAddress}</span>.
      </p>

    

      <PricingTable forOrganizations
        appearance={{
          elements: {
            // Main container
            rootBox: "",
            
            // Card elements
            card: "bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300",
            cardBox: "p-6 space-y-4 ",

            // Headers and text
            planName: "text-2xl font-bold text-gray-900 mb-2",
            planDescription: "text-gray-600 text-sm mb-4",
            planPrice: "text-3xl font-extrabold text-blue-600 mb-1",
            planPricePeriod: "text-gray-500 text-sm",
            
            // Features list
            featureList: "space-y-3 my-6",
            featureListItem: "flex items-center text-gray-700",
            featureListItemIcon: "text-green-500 mr-3 flex-shrink-0",
            featureListItemText: "text-sm",
            
            // Buttons
         
            
            
            
            // Popular/recommended badge
            badge: `
              absolute -top-3 left-1/2 transform -translate-x-1/2 
              bg-gradient-to-r from-blue-400 to-purple-500 
              text-white text-xs font-bold 
              px-4 py-1 rounded-full 
              shadow-md
            `,
            
            // Loading states
            spinner: "text-blue-600",
            
            // Form elements (if any)
            formField: "mb-4",
            formFieldLabel: "block text-sm font-medium text-gray-700 mb-1",
            formFieldInput: `
              w-full px-3 py-2 border border-gray-300 rounded-md 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            `,
            
            // Error states
            formFieldError: "text-red-600 text-sm mt-1",
            
            // Footer elements
            footer: "mt-8 pt-6 border-t border-gray-200",
            footerText: "text-gray-500 text-xs text-center",
            
          
          },
          
          // Layout customizations
          layout: {
            shimmer: true, // Show loading shimmer
          },
          
          // Variable customizations (CSS custom properties)
          variables: {
            colorPrimary: "#2563eb", // Blue-600
            colorBackground: "#ffffff",
            colorText: "#374151", // Gray-700
            colorTextSecondary: "#6b7280", // Gray-500
            colorSuccess: "#10b981", // Green-500
            colorDanger: "#ef4444", // Red-500
            colorWarning: "#f59e0b", // Yellow-500
            borderRadius: "0.5rem",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }
        }}
      />
      {billingInfo && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 mt-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Current Usage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <UsageCard
              title="Uploads"
              used={billingInfo?.usage?.uploads_count || 0}
              limit={billingInfo?.limits?.uploads_count || 0}
              Icon={Upload}
            />
            <UsageCard
              title="Emails per Month"
              used={billingInfo?.usage?.emails || 0}
              limit={billingInfo?.limits?.emails || 0}
              Icon={Mail}
            />
            <UsageCard
              title="Tokens per Month"
              used={billingInfo?.usage?.tokens || 0}
              limit={billingInfo?.limits?.tokens || 0}
              Icon={Cpu}
            />
            <UsageCard
              title="Widget Views per Month"
              used={billingInfo?.usage?.widget_views || 0}
              limit={billingInfo?.limits?.widget_views || 0}
              Icon={Eye}
            />
          </div>
        </div>
      )}
    </div>
  );
}
